"use client"

import React from "react"
import { createContext, useContext, useReducer, useEffect, type ReactNode } from "react"
import { Colors, DarkColors } from "../constants/Colors"

interface ThemeState {
  isDarkMode: boolean
  colors: typeof Colors
}

interface ThemeContextType extends ThemeState {
  toggleTheme: () => void
  setDarkMode: (isDark: boolean) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

type ThemeAction = { type: "TOGGLE_THEME" } | { type: "SET_THEME"; payload: boolean }

const themeReducer = (state: ThemeState, action: ThemeAction): ThemeState => {
  switch (action.type) {
    case "TOGGLE_THEME":
      return {
        ...state,
        isDarkMode: !state.isDarkMode,
        colors: !state.isDarkMode ? DarkColors : Colors,
      }
    case "SET_THEME":
      return {
        ...state,
        isDarkMode: action.payload,
        colors: action.payload ? DarkColors : Colors,
      }
    default:
      return state
  }
}

const initialState: ThemeState = {
  isDarkMode: false,
  colors: Colors,
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(themeReducer, initialState)

  useEffect(() => {
    loadThemePreference()
  }, [])

  useEffect(() => {
    saveThemePreference()
    applyThemeToDOM()
  }, [state.isDarkMode])

  const loadThemePreference = async () => {
    try {
      const savedTheme = localStorage.getItem("admin_isDarkMode")
      if (savedTheme !== null) {
        dispatch({ type: "SET_THEME", payload: JSON.parse(savedTheme) })
      }
    } catch (error) {
      console.error("Error loading theme preference:", error)
    }
  }

  const saveThemePreference = async () => {
    try {
      localStorage.setItem("admin_isDarkMode", JSON.stringify(state.isDarkMode))
    } catch (error) {
      console.error("Error saving theme preference:", error)
    }
  }

  const applyThemeToDOM = () => {
    const htmlElement = document.documentElement
    if (state.isDarkMode) {
      htmlElement.classList.add('dark')
    } else {
      htmlElement.classList.remove('dark')
    }
  }

  const toggleTheme = () => {
    dispatch({ type: "TOGGLE_THEME" })
  }

  const setDarkMode = (isDark: boolean) => {
    dispatch({ type: "SET_THEME", payload: isDark })
  }

  const value: ThemeContextType = {
    ...state,
    toggleTheme,
    setDarkMode,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }
  return context
}
